import {
  PortfolioResolution,
  useAllInvestmentAccountsQuery,
  useConsolidatedPortfolioHistoryQuery,
  usePortfolioHistoryByAccountQuery,
} from '@transactions/graphql';
import {today} from 'common/date';
import {DateTime} from 'luxon';
import * as React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart,
} from 'recharts';
import {
  DateRangeFilter,
  useDateRangeFilter,
} from 'src/components/DateRangeFilter';
import {FilterDropdown} from 'src/components/FilterDropdown';
import {niceTimeTicks, spanDateFormatter} from 'src/lib/chartDateAxis';
import type {DateRangeState} from 'src/components/DateRangeFilter';

type ChartView = 'value' | 'gain';

type Breakdown = 'total' | 'account';

type ManualResolution = 'auto' | 'daily' | 'weekly' | 'monthly';

// The categorical palette has exactly eight validated slots, so the stacked
// view shows at most eight bands: every account when eight or fewer have
// history, otherwise the seven largest by latest value plus an "Other" fold
// (which takes the eighth slot). Colors assign by descending size and stay
// stable while the control is open.
const MAX_STACK_BANDS = 8;
const OTHER_KEY = '__other__';

const RESOLUTION_MAP: Record<string, PortfolioResolution> = {
  daily: PortfolioResolution.Daily,
  weekly: PortfolioResolution.Weekly,
  monthly: PortfolioResolution.Monthly,
};

const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
};

const formatTooltipDate = (timestamp: number): string => {
  // UTC to match the midnight-UTC data points and the (UTC) axis labels, so a
  // hovered point reads the same date as its tick.
  return DateTime.fromMillis(timestamp, {zone: 'utc'}).toFormat('MMM d, yyyy');
};

const generateTicks = (
  data: Array<{timestamp: number}>,
  resolution: PortfolioResolution,
): number[] => {
  if (data.length === 0) return [];

  const minTs = data[0].timestamp;
  const maxTs = data[data.length - 1].timestamp;
  const ticks: number[] = [];

  switch (resolution) {
    case PortfolioResolution.Daily: {
      let current = DateTime.fromMillis(minTs).startOf('day');
      const end = DateTime.fromMillis(maxTs);
      while (current <= end) {
        ticks.push(current.toMillis());
        current = current.plus({days: 5});
      }
      break;
    }
    case PortfolioResolution.Weekly: {
      let current = DateTime.fromMillis(minTs).startOf('month');
      const end = DateTime.fromMillis(maxTs);
      while (current <= end) {
        ticks.push(current.toMillis());
        current = current.plus({months: 1});
      }
      break;
    }
    case PortfolioResolution.Monthly:
    default: {
      let current = DateTime.fromMillis(minTs).startOf('year');
      const end = DateTime.fromMillis(maxTs);
      while (current <= end) {
        ticks.push(current.toMillis());
        current = current.plus({years: 1});
      }
      break;
    }
  }

  return ticks;
};

const getResolution = (dateRange: DateRangeState): PortfolioResolution => {
  const {startDate} = dateRange;
  if (!startDate) {
    return PortfolioResolution.Monthly;
  }

  const now = new Date();
  const diffDays =
    (now.getTime() - startDate.toMillis()) / (1000 * 60 * 60 * 24);

  if (diffDays <= 95) {
    return PortfolioResolution.Daily;
  } else if (diffDays <= 730) {
    return PortfolioResolution.Weekly;
  } else {
    return PortfolioResolution.Monthly;
  }
};

type TooltipProps = {
  active?: boolean;
  payload?: Array<{
    name: string;
    // Null where cost basis and gain are unknown (the line gaps there).
    value: number | null;
    color: string;
    payload: {timestamp: number};
  }>;
  label?: number;
};

const CustomTooltip = ({active, payload, label}: TooltipProps) => {
  if (!active || !payload || label === undefined) return null;

  const dateStr = formatTooltipDate(label);

  return (
    <div className="border-border bg-card rounded-lg border p-3 shadow-lg">
      <p className="text-foreground mb-2 font-medium">{dateStr}</p>
      {payload.map((entry, index) => (
        <p key={index} style={{color: entry.color}} className="text-sm">
          {entry.name}: {entry.value == null ? '—' : formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
};

type PortfolioChartBaseProps = {
  accountId?: string;
  emptyMessage?: string;
};

export const PortfolioChartBase = ({
  accountId,
  emptyMessage = 'Not enough data for chart. Add portfolio snapshots to see history.',
}: PortfolioChartBaseProps) => {
  const id = React.useId();
  const gainGradientId = `${id}-gain`;
  const lossGradientId = `${id}-loss`;

  const {dateRange, setDateRange} = useDateRangeFilter('3M');
  const [chartView, setChartView] = React.useState<ChartView>('value');
  const [breakdown, setBreakdown] = React.useState<Breakdown>('total');
  const [manualResolution, setManualResolution] =
    React.useState<ManualResolution>('auto');

  const autoResolution = getResolution(dateRange);
  const resolution =
    manualResolution === 'auto'
      ? autoResolution
      : RESOLUTION_MAP[manualResolution];

  const startDate = dateRange.startDate?.toISODate() ?? undefined;
  // Open-ended presets clamp at today: recordBalanceAnchor permits
  // future-dated anchors, and the chart must not extend past today to a
  // value the summary and cards (all today-clamped) disagree with.
  const endDate =
    dateRange.endDate?.toISODate() ?? today().toISODate() ?? undefined;

  // cache-and-network: snapshot and transaction edits happen on other tabs,
  // so re-mounting the chart shows cached data instantly while refreshing.
  const {data, loading} = useConsolidatedPortfolioHistoryQuery({
    variables: {accountId, resolution, startDate, endDate},
    fetchPolicy: 'cache-and-network',
  });

  const rangedHistory = data?.consolidatedPortfolioHistory ?? [];

  // If the selected range has too few snapshots to chart (e.g. a closed
  // account whose history predates the window), fall back to all history.
  const needsFallback =
    !loading && startDate !== undefined && rangedHistory.length < 2;
  const {data: fallbackData, loading: fallbackLoading} =
    useConsolidatedPortfolioHistoryQuery({
      variables: {accountId, resolution: PortfolioResolution.Monthly, endDate},
      skip: !needsFallback,
    });
  const fallbackHistory = fallbackData?.consolidatedPortfolioHistory ?? [];
  const usingFallback = needsFallback && fallbackHistory.length >= 2;

  // By-account breakdown: only meaningful on the consolidated chart (no
  // accountId) and only in the Value view — gain is defined portfolio-wide.
  // Its variables track the effective range: when the total line falls back
  // to all history, the stack must follow, or the pill would claim a
  // breakdown while silently rendering the total.
  const breakdownActive =
    breakdown === 'account' && accountId == null && chartView === 'value';
  const {
    data: byAccountData,
    loading: byAccountLoading,
    error: byAccountError,
  } = usePortfolioHistoryByAccountQuery({
    variables: usingFallback
      ? {resolution: PortfolioResolution.Monthly, endDate}
      : {resolution, startDate, endDate},
    fetchPolicy: 'cache-and-network',
    skip: !breakdownActive,
  });
  const {data: accountsData} = useAllInvestmentAccountsQuery({
    skip: !breakdownActive,
  });

  // Stacked rows: the union of every series' dates, each account
  // forward-filled onto it (zero before an account's first point, matching
  // how the consolidated history treats absent accounts).
  const stacked = React.useMemo(() => {
    const series = byAccountData?.portfolioHistoryByAccount ?? [];
    if (!breakdownActive || series.length === 0) return null;
    const nameOf = new Map(
      (accountsData?.allInvestmentAccounts ?? []).map((account) => [
        account._id,
        account.name,
      ]),
    );
    // Rank by CURRENT balance, not the ranged series' endpoint: palette
    // slots (and which accounts fold into Other) must not reshuffle when
    // the user changes the date range — color follows the account.
    const balanceOf = new Map(
      (accountsData?.allInvestmentAccounts ?? []).map((account) => [
        account._id,
        account.balance,
      ]),
    );
    const ranked = [...series].sort(
      (a, b) =>
        (balanceOf.get(b.accountId) ?? 0) - (balanceOf.get(a.accountId) ?? 0),
    );
    const foldNeeded = ranked.length > MAX_STACK_BANDS;
    const top = foldNeeded ? ranked.slice(0, MAX_STACK_BANDS - 1) : ranked;
    const rest = foldNeeded ? ranked.slice(MAX_STACK_BANDS - 1) : [];

    const dates = Array.from(
      new Set(series.flatMap((s) => s.points.map((p) => p.date))),
    ).sort();
    // One ascending cursor per series makes the fill linear overall.
    const cursors = new Map(series.map((s) => [s.accountId, {index: 0, value: 0}]));
    const valueAt = (s: (typeof series)[number], date: string): number => {
      const cursor = cursors.get(s.accountId);
      if (cursor == null) return 0;
      while (
        cursor.index < s.points.length &&
        s.points[cursor.index].date <= date
      ) {
        cursor.value = s.points[cursor.index].portfolioValue;
        cursor.index++;
      }
      return cursor.value;
    };
    const rows = dates.map((date) => {
      const row: Record<string, number | string> = {
        timestamp: new Date(date).getTime(),
        fullDate: date,
      };
      for (const s of top) {
        row[s.accountId] = valueAt(s, date);
      }
      if (rest.length > 0) {
        row[OTHER_KEY] = rest.reduce((sum, s) => sum + valueAt(s, date), 0);
      }
      return row;
    });
    const bands = top.map((s, index) => ({
      key: s.accountId,
      name: nameOf.get(s.accountId) ?? 'Unknown account',
      // The raw --chart-N custom properties are the runtime-resolvable ones
      // (the @theme inline aliases are compile-time only).
      color: `var(--chart-${index + 1})`,
    }));
    if (rest.length > 0) {
      bands.push({
        key: OTHER_KEY,
        name: `Other (${rest.length})`,
        color: `var(--chart-${MAX_STACK_BANDS})`,
      });
    }
    return {rows, bands};
  }, [breakdownActive, byAccountData, accountsData]);

  // Only block on loading when there is nothing cached to draw — a
  // background refresh keeps the previous chart on screen.
  if ((loading && !data) || (needsFallback && fallbackLoading)) {
    return (
      <div className="border-border bg-card flex h-64 items-center justify-center rounded-lg border">
        <span className="text-muted-foreground">Loading chart...</span>
      </div>
    );
  }

  const history = usingFallback ? fallbackHistory : rangedHistory;
  const effectiveResolution = usingFallback
    ? PortfolioResolution.Monthly
    : resolution;

  if (history.length < 2) {
    return (
      <div className="border-border bg-card flex h-64 items-center justify-center rounded-lg border">
        <span className="text-muted-foreground">{emptyMessage}</span>
      </div>
    );
  }

  const lastSnapshotLabel = DateTime.fromISO(
    history[history.length - 1].date,
  ).toFormat('MMM d, yyyy');

  const chartData = history.map((point) => ({
    timestamp: new Date(point.date).getTime(),
    fullDate: point.date,
    portfolioValue: point.portfolioValue,
    // Null (unknown) from a transfer of unpriced shares on: the cost basis
    // line and gain area gap there rather than drawing 0 (connectNulls is
    // off by default).
    costBasis: point.costBasis,
    gain: point.gain,
  }));

  const firstTs = chartData[0].timestamp;
  const lastTs = chartData[chartData.length - 1].timestamp;
  const spanDays = (lastTs - firstTs) / 86_400_000;
  const formatTickDate = spanDateFormatter(firstTs, lastTs);
  // Once the labels coarsen to "MMM yyyy"/year, the ticks have to coarsen with
  // them: otherwise a multi-year range viewed at the Weekly or Daily scale
  // keeps monthly/5-day ticks and repeats the same year label many times over.
  // Shorter spans keep the resolution-based ticks the scale control drives.
  const ticks =
    spanDays > 90
      ? niceTimeTicks(firstTs, lastTs)
      : generateTicks(chartData, effectiveResolution);

  return (
    <div className="border-border bg-card rounded-lg border p-4">
      {/* Controls are filter pills (same pattern as the accounts list's
          status/type dropdowns) so they wrap on narrow screens instead of
          forcing a horizontal scroll. The custom-range date inputs are
          basis-full, dropping to their own row inside this flex-wrap. */}
      <div className="mb-4 flex flex-wrap items-center gap-x-1 gap-y-2">
        <h3 className="text-foreground mr-auto font-medium">
          Portfolio history
        </h3>
        {/* Breakdown only applies to the consolidated Value view — the
            account detail chart is one account, and gain is portfolio-wide. */}
        {accountId == null && chartView === 'value' && (
          <FilterDropdown
            label="Breakdown"
            allLabel="Total"
            options={[{key: 'account', label: 'By account'}]}
            value={breakdown === 'total' ? 'all' : 'account'}
            onChange={(key) =>
              setBreakdown(key === 'account' ? 'account' : 'total')
            }
          />
        )}
        <FilterDropdown
          label="View"
          allLabel="Value"
          options={[{key: 'gain', label: 'Gain/Loss'}]}
          value={chartView === 'value' ? 'all' : 'gain'}
          onChange={(key) => setChartView(key === 'gain' ? 'gain' : 'value')}
        />
        <FilterDropdown
          label="Scale"
          allLabel="Auto"
          options={[
            {key: 'daily', label: 'Daily'},
            {key: 'weekly', label: 'Weekly'},
            {key: 'monthly', label: 'Monthly'},
          ]}
          value={manualResolution === 'auto' ? 'all' : manualResolution}
          onChange={(key) =>
            setManualResolution(
              key === 'daily' || key === 'weekly' || key === 'monthly'
                ? key
                : 'auto',
            )
          }
        />
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
      </div>
      {usingFallback && (
        <div className="mb-3 rounded-md border border-amber-500/50 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
          No snapshots in the selected range — showing all available history.
          Data is not current: last snapshot {lastSnapshotLabel}.
        </div>
      )}
      {/* An active breakdown with nothing stackable must say so — silently
          rendering the Total line under a "By account" pill would present
          the wrong visualization as the selected mode. */}
      {breakdownActive && (stacked == null || stacked.rows.length < 2) ? (
        <div className="flex h-[300px] items-center justify-center">
          <span className="text-muted-foreground">
            {byAccountLoading && byAccountData == null
              ? 'Loading breakdown...'
              : byAccountError != null
                ? 'Couldn’t load the by-account breakdown.'
                : 'Not enough data for a by-account breakdown.'}
          </span>
        </div>
      ) : (
      <ResponsiveContainer width="100%" height={300}>
        {breakdownActive && stacked != null && stacked.rows.length >= 2 ? (
          <AreaChart
            data={stacked.rows}
            margin={{top: 10, right: 30, left: 20, bottom: 10}}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--color-border)"
              strokeWidth={1.5}
            />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              ticks={ticks}
              tickFormatter={formatTickDate}
              tick={{fill: 'var(--color-muted-foreground)', fontSize: 12}}
              tickLine={{stroke: 'var(--color-border)'}}
              axisLine={{stroke: 'var(--color-border)'}}
            />
            <YAxis
              tick={{fill: 'var(--color-muted-foreground)', fontSize: 12}}
              tickFormatter={formatCurrency}
              tickLine={{stroke: 'var(--color-border)'}}
              axisLine={{stroke: 'var(--color-border)'}}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{paddingTop: '10px'}} />
            {/* Largest band renders first, so it sits at the bottom of the
                stack and the small accounts ride on top. */}
            {stacked.bands.map((band) => (
              <Area
                key={band.key}
                type="monotone"
                dataKey={band.key}
                name={band.name}
                stackId="portfolio"
                stroke={band.color}
                strokeWidth={1}
                fill={band.color}
                fillOpacity={0.55}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        ) : chartView === 'value' ? (
          <LineChart
            data={chartData}
            margin={{top: 10, right: 30, left: 20, bottom: 10}}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--color-border)"
              strokeWidth={1.5}
            />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              ticks={ticks}
              tickFormatter={formatTickDate}
              tick={{fill: 'var(--color-muted-foreground)', fontSize: 12}}
              tickLine={{stroke: 'var(--color-border)'}}
              axisLine={{stroke: 'var(--color-border)'}}
            />
            <YAxis
              tick={{fill: 'var(--color-muted-foreground)', fontSize: 12}}
              tickFormatter={formatCurrency}
              tickLine={{stroke: 'var(--color-border)'}}
              axisLine={{stroke: 'var(--color-border)'}}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{
                paddingTop: '10px',
              }}
            />
            <ReferenceLine y={0} stroke="var(--color-border)" />
            <Line
              type="monotone"
              dataKey="portfolioValue"
              name="Portfolio value"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              activeDot={{r: 4, fill: '#3b82f6'}}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="costBasis"
              name="Cost basis"
              stroke="#6b7280"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={{r: 4, fill: '#6b7280'}}
              isAnimationActive={false}
            />
          </LineChart>
        ) : (
          <AreaChart
            data={chartData}
            margin={{top: 10, right: 30, left: 20, bottom: 10}}
          >
            <defs>
              <linearGradient id={gainGradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
              </linearGradient>
              <linearGradient id={lossGradientId} x1="0" y1="1" x2="0" y2="0">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--color-border)"
              strokeWidth={1.5}
            />
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              ticks={ticks}
              tickFormatter={formatTickDate}
              tick={{fill: 'var(--color-muted-foreground)', fontSize: 12}}
              tickLine={{stroke: 'var(--color-border)'}}
              axisLine={{stroke: 'var(--color-border)'}}
            />
            <YAxis
              tick={{fill: 'var(--color-muted-foreground)', fontSize: 12}}
              tickFormatter={formatCurrency}
              tickLine={{stroke: 'var(--color-border)'}}
              axisLine={{stroke: 'var(--color-border)'}}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="var(--color-border)" strokeWidth={2} />
            <Area
              type="monotone"
              dataKey="gain"
              name="Gain/Loss"
              stroke="#22c55e"
              strokeWidth={2}
              fill={`url(#${gainGradientId})`}
              activeDot={{r: 4}}
              isAnimationActive={false}
            />
          </AreaChart>
        )}
      </ResponsiveContainer>
      )}
    </div>
  );
};
